import React, { useEffect, useState } from 'react';
import apiClient from '../../api/client';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { 
  Calendar, CheckCircle, XCircle, 
  Clock, UserPlus, AlertTriangle, DollarSign, Users, Activity
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await apiClient.get('/admin/dashboard');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ko-KR').format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('ko-KR', { timeZone: 'Asia/Seoul', month: 'short', day: 'numeric' });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('ko-KR', { timeZone: 'Asia/Seoul', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen" data-testid="admin-dashboard">
      <h1 className="text-2xl font-bold mb-6 text-gray-800">대시보드</h1>
      
      {/* 상단 3카드: 매출만 표시 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium opacity-90">오늘 매출</CardTitle>
            <DollarSign className="h-5 w-5 opacity-80" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{formatCurrency(stats?.today_sales)}원</div>
            <p className="text-xs opacity-80 mt-1">결제 기준 (환불 포함)</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white border-0">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium opacity-90">이번 달 매출</CardTitle>
            <DollarSign className="h-5 w-5 opacity-80" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{formatCurrency(stats?.this_month_sales)}원</div>
            <p className="text-xs opacity-80 mt-1">결제 기준 (환불 포함)</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-violet-500 to-violet-600 text-white border-0">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium opacity-90">지난 달 매출</CardTitle>
            <DollarSign className="h-5 w-5 opacity-80" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{formatCurrency(stats?.last_month_sales)}원</div>
            <p className="text-xs opacity-80 mt-1">결제 기준 (환불 포함)</p>
          </CardContent>
        </Card>
      </div>

      {/* 중간 2컬럼: 오늘 수업 & 운영 요약 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {/* 좌측: 오늘 수업 리스트 */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Clock className="h-4 w-4 text-blue-600" />
              오늘 수업 일정
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.today_classes?.length > 0 ? (
              <div className="space-y-3 max-h-[280px] overflow-y-auto pr-1">
                {stats.today_classes.map((cls, idx) => (
                  <div 
                    key={idx} 
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                    onClick={() => navigate(`/admin/sessions/${cls._id || cls.id}`)}
                    data-testid={`today-class-${idx}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="text-sm font-semibold text-blue-600 w-14">
                        {formatTime(cls.scheduled_at)}
                      </div>
                      <div>
                        <div className="font-medium text-gray-800">{cls.class_name || cls.name}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-medium ${
                        (cls.reservation_count || cls.current_count || 0) >= cls.max_capacity 
                          ? 'text-red-600' 
                          : 'text-gray-600'
                      }`}>
                        {cls.reservation_count ?? cls.current_count ?? 0}/{cls.max_capacity}명
                      </span>
                      {cls.status === 'completed' && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">완료</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400">
                <Calendar className="h-10 w-10 mx-auto mb-2 opacity-50" />
                <p>오늘 예정된 수업이 없습니다</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* 우측: 운영 요약 (단순화) */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Activity className="h-4 w-4 text-emerald-600" />
              운영 요약
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Calendar className="h-4 w-4 text-blue-600" />
                  <span className="text-sm text-gray-600">오늘 수업 수</span>
                </div>
                <div className="text-2xl font-bold text-blue-700">
                  {stats?.today_sessions_count || 0}개
                </div>
              </div>
              <div className="p-4 bg-emerald-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="h-4 w-4 text-emerald-600" />
                  <span className="text-sm text-gray-600">총 예약 인원</span>
                </div>
                <div className="text-2xl font-bold text-emerald-700">
                  {stats?.total_reservations_today || 0}명
                </div>
              </div>
              <div className="p-4 bg-green-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-sm text-gray-600">출석 완료</span>
                </div>
                <div className="text-2xl font-bold text-green-700">
                  {stats?.attended_count || 0}명
                </div>
              </div>
              <div className="p-4 bg-red-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <XCircle className="h-4 w-4 text-red-600" />
                  <span className="text-sm text-gray-600">노쇼</span>
                </div>
                <div className="text-2xl font-bold text-red-700">
                  {stats?.no_show_count || 0}명
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* 하단 3컬럼: 리스트 (높이 고정 + 스크롤) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* 최근 등록 회원 */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <UserPlus className="h-4 w-4 text-blue-600" />
              최근 등록 회원
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="max-h-[300px] overflow-y-auto">
              {stats?.recent_members?.length > 0 ? (
                <ul className="space-y-2">
                  {stats.recent_members.map((member, idx) => (
                    <li 
                      key={idx} 
                      className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0 hover:bg-gray-50 px-2 rounded cursor-pointer"
                      onClick={() => navigate(`/admin/members`)}
                      data-testid={`recent-member-${idx}`}
                    >
                      <div>
                        <div className="font-medium text-gray-800">{member.name}</div>
                        <div className="text-xs text-gray-500">{member.member_number}</div>
                      </div>
                      <span className="text-xs text-gray-400">
                        {formatDate(member.created_at)}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-center py-4 text-gray-400 text-sm">최근 등록 회원 없음</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* 이용권 만료 임박 */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              이용권 만료 임박
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="max-h-[300px] overflow-y-auto">
              {stats?.expiring_members?.length > 0 ? (
                <ul className="space-y-2">
                  {stats.expiring_members.map((member, idx) => (
                    <li 
                      key={idx} 
                      className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0 hover:bg-gray-50 px-2 rounded cursor-pointer"
                      onClick={() => navigate(`/admin/members`)}
                      data-testid={`expiring-member-${idx}`}
                    >
                      <div>
                        <div className="font-medium text-gray-800">{member.name}</div>
                        <div className="text-xs text-[#A1A1AA]">{member.pass_name || '이용권'}</div>
                      </div>
                      <span className="text-xs font-medium text-amber-600">
                        {member.days_left}일 남음
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-center py-4 text-gray-400 text-sm">만료 임박 회원 없음</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* 장기 미출석 회원 */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold flex items-center justify-between w-full">
              <div className="flex items-center gap-2">
                <XCircle className="h-4 w-4 text-red-600" />
                장기 미출석 회원
              </div>
              <span className="text-xs font-normal text-[#A1A1AA]">미출석 기준: 14일</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="max-h-[300px] overflow-y-auto">
              {stats?.inactive_members?.length > 0 ? (
                <ul className="space-y-2">
                  {stats.inactive_members.map((member, idx) => (
                    <li 
                      key={idx} 
                      className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0 hover:bg-gray-50 px-2 rounded cursor-pointer"
                      onClick={() => navigate(`/admin/members`)}
                      data-testid={`inactive-member-${idx}`}
                    >
                      <div>
                        <div className="font-medium text-gray-800">{member.name}</div>
                        <div className="text-xs text-gray-500">{member.member_number}</div>
                      </div>
                      <span className="text-xs text-gray-400">
                        {member.last_attendance}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-center py-4 text-gray-400 text-sm">장기 미출석 회원 없음</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
